import React, { useState } from 'react';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { authApi, dashboardApi } from '../lib/api';
import { User, Key, Sliders, Database, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const SettingsPage = () => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [providerStatus, setProviderStatus] = useState(null);
  const [checkingProviders, setCheckingProviders] = useState(false);
  
  const [preferences, setPreferences] = useState({
    preferred_sectors: user?.preferred_sectors || [],
    preferred_stages: user?.preferred_stages || [],
    scoring_weights: user?.scoring_weights || {
      opportunity: 1.0,
      timing: 1.0,
      founder: 1.5,
      product: 1.0,
      market: 1.0,
      fundability: 0.8,
      non_consensus: 1.2,
      introability: 0.8
    }
  });

  const sectors = ['AI/ML', 'Fintech', 'SaaS', 'Healthcare', 'Infrastructure', 'Consumer', 'Enterprise', 'Crypto/Web3', 'Climate'];
  const stages = ['early', 'growth', 'late'];

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await authApi.updatePreferences(preferences);
    } catch (err) {
      console.error('Failed to save preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCheckProviders = async () => {
    setCheckingProviders(true);
    try {
      const response = await dashboardApi.getProviderStatus();
      setProviderStatus(response.data.providers);
    } catch (err) {
      console.error('Failed to check providers:', err);
    } finally {
      setCheckingProviders(false);
    }
  };

  const toggleSector = (sector) => {
    const current = preferences.preferred_sectors;
    if (current.includes(sector)) {
      setPreferences({ ...preferences, preferred_sectors: current.filter(s => s !== sector) });
    } else {
      setPreferences({ ...preferences, preferred_sectors: [...current, sector] });
    }
  };

  const toggleStage = (stage) => {
    const current = preferences.preferred_stages;
    if (current.includes(stage)) {
      setPreferences({ ...preferences, preferred_stages: current.filter(s => s !== stage) });
    } else {
      setPreferences({ ...preferences, preferred_stages: [...current, stage] });
    }
  };

  const updateWeight = (key, value) => {
    setPreferences({
      ...preferences,
      scoring_weights: { ...preferences.scoring_weights, [key]: parseFloat(value) || 0 }
    });
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-light font-['Outfit'] text-zinc-50">Settings</h1>
          <p className="text-zinc-400 font-['Manrope']">Manage your preferences and integrations</p>
        </div>

        <Tabs defaultValue="profile">
          <TabsList className="bg-zinc-900 border border-zinc-800 mb-6">
            <TabsTrigger value="profile" className="data-[state=active]:bg-zinc-800">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="preferences" className="data-[state=active]:bg-zinc-800">
              <Sliders className="w-4 h-4 mr-2" />
              Preferences
            </TabsTrigger>
            <TabsTrigger value="integrations" className="data-[state=active]:bg-zinc-800">
              <Key className="w-4 h-4 mr-2" />
              Integrations
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
              <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-6">Profile Information</h2>
              
              <div className="flex items-start gap-6 mb-6">
                <div className="w-20 h-20 bg-zinc-800 rounded-full overflow-hidden flex-shrink-0">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-500 text-2xl font-mono">
                      {user?.name?.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-zinc-50 font-medium font-['Outfit'] text-lg">{user?.name}</h3>
                  <p className="text-zinc-400 font-['Manrope']">{user?.email}</p>
                  <p className="text-zinc-500 text-sm mt-2">Signed in with Google</p>
                </div>
              </div>

              <p className="text-zinc-500 text-sm">Profile information is managed through your Google account.</p>
            </div>
          </TabsContent>

          <TabsContent value="preferences">
            <div className="space-y-6">
              {/* Sectors */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
                <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-4">Preferred Sectors</h2>
                <p className="text-zinc-400 text-sm mb-4">Select sectors you're most interested in</p>
                <div className="flex flex-wrap gap-2">
                  {sectors.map((sector) => (
                    <button
                      key={sector}
                      onClick={() => toggleSector(sector)}
                      data-testid={`pref-sector-${sector}`}
                      className={`px-3 py-1.5 text-sm font-mono rounded-md border transition-colors ${
                        preferences.preferred_sectors.includes(sector)
                          ? 'bg-white text-zinc-900 border-white'
                          : 'text-zinc-400 border-zinc-700 hover:border-zinc-500'
                      }`}
                    >
                      {sector}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stages */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
                <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-4">Preferred Stages</h2>
                <p className="text-zinc-400 text-sm mb-4">Select investment stages you focus on</p>
                <div className="flex gap-2">
                  {stages.map((stage) => (
                    <button
                      key={stage}
                      onClick={() => toggleStage(stage)}
                      data-testid={`pref-stage-${stage}`}
                      className={`px-4 py-2 text-sm font-mono rounded-md border transition-colors capitalize ${
                        preferences.preferred_stages.includes(stage)
                          ? 'bg-white text-zinc-900 border-white'
                          : 'text-zinc-400 border-zinc-700 hover:border-zinc-500'
                      }`}
                    >
                      {stage}
                    </button>
                  ))}
                </div>
              </div>

              {/* Scoring Weights */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
                <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-4">Conviction Scoring Weights</h2>
                <p className="text-zinc-400 text-sm mb-4">Adjust how much each factor matters in your rankings</p>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(preferences.scoring_weights).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <label className="text-zinc-400 text-sm capitalize">{key.replace('_', ' ')}</label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="2"
                        value={value}
                        onChange={(e) => updateWeight(key, e.target.value)}
                        data-testid={`weight-${key}`}
                        className="w-20 bg-zinc-800 border-zinc-700 text-zinc-50 text-center"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                onClick={handleSavePreferences} 
                disabled={saving}
                data-testid="save-preferences-btn"
                className="bg-white text-zinc-900 hover:bg-zinc-200"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="integrations">
            <div className="space-y-6">
              {/* Provider Status */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50">Data Providers</h2>
                    <p className="text-zinc-400 text-sm">Status of connected data sources</p>
                  </div>
                  <Button 
                    onClick={handleCheckProviders}
                    disabled={checkingProviders}
                    variant="outline"
                    data-testid="check-providers-btn"
                    className="border-zinc-700 text-zinc-400"
                  >
                    {checkingProviders ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
                    Check Status
                  </Button>
                </div>

                {providerStatus && (
                  <div className="space-y-3">
                    {Object.entries(providerStatus).map(([name, status]) => (
                      <div key={name} className="flex items-center justify-between p-3 bg-zinc-800 rounded-md">
                        <div className="flex items-center gap-3">
                          {status.status === 'available' ? (
                            <CheckCircle className="w-5 h-5 text-emerald-400" />
                          ) : (
                            <XCircle className="w-5 h-5 text-rose-400" />
                          )}
                          <span className="text-zinc-50 font-mono capitalize">{name.replace('_', ' ')}</span>
                        </div>
                        <span className={`text-xs font-mono ${status.status === 'available' ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {status.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* API Keys */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
                <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-4">API Keys</h2>
                <p className="text-zinc-400 text-sm mb-4">Add your API keys to enable additional data sources</p>
                
                <div className="space-y-4">
                  {['Crunchbase', 'GitHub', 'News API'].map((provider) => (
                    <div key={provider} className="flex items-center gap-4">
                      <label className="w-32 text-zinc-400 text-sm">{provider}</label>
                      <Input
                        type="password"
                        placeholder="Enter API key"
                        data-testid={`api-key-${provider.toLowerCase().replace(' ', '-')}`}
                        className="flex-1 bg-zinc-800 border-zinc-700 text-zinc-50"
                      />
                    </div>
                  ))}
                </div>
                <p className="text-zinc-500 text-xs mt-4">API keys are stored securely and only used for data enrichment.</p>
              </div>

              {/* Gmail Integration */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
                <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-4">Gmail Integration</h2>
                <p className="text-zinc-400 text-sm mb-4">Connect your Gmail account to send outreach emails</p>
                
                <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-md">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-zinc-700 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-zinc-50 font-medium">Gmail</p>
                      <p className="text-zinc-500 text-xs">Not connected</p>
                    </div>
                  </div>
                  <Button variant="outline" className="border-zinc-700 text-zinc-400">
                    Connect
                  </Button>
                </div>
                <p className="text-zinc-500 text-xs mt-4">Gmail integration requires additional setup. Contact support for assistance.</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default SettingsPage;
